import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import LaunchingSoonCard from "@/components/site/LaunchingSoonCard";
import { MaskedLines } from "@/components/site/Reveal";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "onekind", label: "OneKind" },
  { key: "oneskin", label: "One Skin" },
];

const IMG = {
  pharma: [
    "https://images.pexels.com/photos/13779111/pexels-photo-13779111.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/15897784/pexels-photo-15897784.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/3652097/pexels-photo-3652097.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/6941883/pexels-photo-6941883.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ],
  skin: [
    "https://images.pexels.com/photos/16378451/pexels-photo-16378451.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/7670676/pexels-photo-7670676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/8154099/pexels-photo-8154099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/6621462/pexels-photo-6621462.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ],
};

function tilesFor(brand) {
  if (brand === "onekind") return IMG.pharma.map((img) => ({ img, tone: "pharma", label: "OneKind" }));
  if (brand === "oneskin") return IMG.skin.map((img) => ({ img, tone: "skin", label: "One Skin" }));
  return [
    { img: IMG.pharma[0], tone: "pharma", label: "OneKind" },
    { img: IMG.skin[0], tone: "skin", label: "One Skin" },
    { img: IMG.pharma[1], tone: "pharma", label: "OneKind" },
    { img: IMG.skin[1], tone: "skin", label: "One Skin" },
  ];
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const brand = params.get("brand") || "all";

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [brand]);

  const setBrand = (b) => setParams(b === "all" ? {} : { brand: b });
  const tiles = tilesFor(brand);

  return (
    <main data-testid="shop-page" className="pt-32 md:pt-40 pb-24 px-6 md:px-12 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">The Collection</span>
        <h1 className="font-serif text-6xl md:text-8xl tracking-tight leading-[0.85] mt-4 mb-6">
          <MaskedLines animate lines={["Launching", <span key="s" className="text-derma italic">soon.</span>]} />
        </h1>
        <p className="font-sans text-base md:text-lg text-ink/60 max-w-xl mb-10">
          Our OneKind and One Skin ranges are being finalised to clinical standards. Register your
          interest and we&apos;ll notify you the moment they&apos;re available.
        </p>

        <div className="flex flex-wrap items-center justify-between gap-6 border-y border-ink/10 py-5 mb-12">
          <div className="flex gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setBrand(f.key)}
                data-testid={`shop-filter-${f.key}`}
                className={`rounded-full px-5 py-2 text-xs uppercase tracking-widest font-sans transition-colors ${
                  brand === f.key ? "bg-pharma text-stone" : "border border-ink/15 hover:border-ink/40"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <a
            href="https://inoneskin.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shop-inoneskin-link"
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-derma hover:text-derma-dark transition-colors"
          >
            Shop One Skin at inoneskin.com <ArrowUpRight size={14} />
          </a>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
          {tiles.map((t, i) => (
            <LaunchingSoonCard key={`${brand}-${i}`} image={t.img} tone={t.tone} label={t.label} index={i} />
          ))}
        </div>

        <div className="mt-20 flex flex-col items-center text-center gap-6 border-t border-ink/10 pt-16">
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight max-w-2xl leading-tight">
            Be first to know when we launch.
          </h2>
          <button
            onClick={() => navigate("/#connect")}
            data-testid="shop-register-interest"
            className="rounded-full px-10 py-4 bg-derma text-stone text-xs uppercase tracking-widest font-sans hover:bg-derma-dark transition-colors"
          >
            Register Interest
          </button>
        </div>
      </div>
    </main>
  );
}
